const REDACTED = '[REDACTED]';
const secrets = new Set<string>();

// Keep previous credentials for late messages from a disconnected phone too.
// These values stay in memory and are never written to localStorage.
export const registerSipLogSecret = (secret?: string) => {
  if (!secret) return;
  secrets.add(secret);
  secrets.add(JSON.stringify(secret).slice(1, -1));
  try {
    secrets.add(encodeURIComponent(secret));
  } catch {
    // A malformed Unicode string can still be redacted in its original form.
  }
};

const sensitiveKey = (key: string) =>
  /^(password|passwd|pwd|ha1|authorizationjwt|authorization|proxyauthorization|secret|apisecret|apikey|token|accesstoken|refreshtoken|credentials)$/i.test(
    key.replace(/[^a-z0-9]/gi, '')
  );

const redactText = (text: string): string => {
  let result = text;
  for (const secret of [...secrets].sort((a, b) => b.length - a.length)) {
    result = result.split(secret).join(REDACTED);
  }
  return result
    .replace(
      /(^|\r?\n)([ \t]*(?:proxy-)?authorization\s*:\s*)[^\r\n]*/gi,
      `$1$2${REDACTED}`
    )
    .replace(
      /(["']?(?:password|passwd|pwd|ha1|authorization_jwt|secret|api[-_]?secret|api[-_]?key|(?:access[-_]?|refresh[-_]?)?token)["']?\s*[:=]\s*)("(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*'|[^\s,;}\]]+)/gi,
      `$1${REDACTED}`
    );
};

const snapshot = (
  value: unknown,
  seen: WeakSet<object>,
  depth = 0
): unknown => {
  if (typeof value === 'string') return redactText(value);
  if (value === null) return null;
  if (typeof value === 'function') return '[Function omitted]';
  if (typeof value !== 'object') return redactText(String(value));
  if (depth >= 6) return '[Object omitted]';
  if (seen.has(value)) return '[Circular]';
  seen.add(value);

  // Read data properties only. Never invoke getters or toJSON on SDK objects.
  const result: Record<string, unknown> = Object.create(null);
  for (const [key, descriptor] of Object.entries(
    Object.getOwnPropertyDescriptors(value)
  )) {
    result[redactText(key)] = sensitiveKey(key)
      ? REDACTED
      : 'value' in descriptor
        ? snapshot(descriptor.value, seen, depth + 1)
        : '[Getter omitted]';
  }
  return result;
};

export const formatSipLogMessage = (args: readonly unknown[]): string => {
  try {
    const values = args.map((value) => {
      const safe = snapshot(value, new WeakSet());
      return typeof safe === 'string' ? safe : JSON.stringify(safe);
    });
    let index = 1;
    const message = (values[0] ?? '').replace(
      /%([%sdifoOj])/g,
      (match, type) => {
        if (type === '%') return '%';
        return index < values.length ? values[index++]! : match;
      }
    );
    // Redact again after interpolation, including any escaped JSON strings.
    return redactText([message, ...values.slice(index)].join(' ')).slice(
      0,
      8192
    );
  } catch {
    // An uninspectable log must not interrupt registration or expose raw data.
    return '[SIP log omitted]';
  }
};
